import React from "react";
import styled from "@emotion/styled";
import {
  FONT_SIZE_MEDIUM,
  BORDER_RADIUS_DEFAULT,
  COLOR_TERTIARY,
  COLOR_PRIMARY,
  FONT_SIZE_SMALL,
  COLOR_ERROR,
  PLACEHOLDER_TEXT_COLOR,
  HEADER_FONT_FAMILY,
  DISABLED_INPUT_FIELD_COLOR,
  COLOR_INPUT_DEFAULT_BORDER,
  COLOR_INPUT_BORDER_FOCUSED,
  COLOR_INPUT_BORDER_HOVER,
} from "../theme";
import { ParagraphHeaderSmall } from "../typography/Paragraph";

export const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledInput = styled.input<{
  hasError?: boolean;
  disabled?: boolean;
}>`
  height: 56px;
  padding: 0 16px;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  font-size: ${FONT_SIZE_MEDIUM};
  font-family: ${HEADER_FONT_FAMILY};
  background-color: ${(props) =>
    props.disabled ? DISABLED_INPUT_FIELD_COLOR : COLOR_TERTIARY};
  outline: none;
  border: ${({ hasError }) =>
    hasError
      ? `1px solid ${COLOR_ERROR}`
      : `1px solid ${COLOR_INPUT_DEFAULT_BORDER}`};
  color: ${COLOR_PRIMARY};
  font-weight: 500;

  /* Fix for mobile browsers to ensure box-shadow is visible on date/time inputs */
  &[type="date"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  &[value=""]:not(:focus) {
    color: ${PLACEHOLDER_TEXT_COLOR};

    /* Safari-specific override - let Safari handle its own placeholder color */
    @supports (-webkit-hyphens: none) {
      color: inherit;
    }
  }
  &:focus {
    ${({ hasError }) =>
      !hasError && `border: 1px solid ${COLOR_INPUT_BORDER_FOCUSED};`}
  }

  &:hover {
    ${({ hasError }) =>
      !hasError && `border: 1px solid ${COLOR_INPUT_BORDER_HOVER};`}
  }
`;

export const ErrorText = styled(ParagraphHeaderSmall)`
  color: ${COLOR_ERROR};
  font-size: ${FONT_SIZE_SMALL};
  margin: 0;
  padding: 8px 16px;
`;

export interface InputFieldProps {
  type: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onWheel?: (e: React.WheelEvent<HTMLInputElement>) => void;
  required?: boolean;
  errorMessage?: string | null;
  disabled?: boolean;
  className?: string;
  id?: string;
  min?: string;
  max?: string;
  step?: string;
  dataTestId?: string;
  inputMode?: "none" | "numeric" | "decimal";
}

export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      type,
      placeholder,
      value,
      onChange,
      onBlur,
      onFocus,
      onWheel,
      className,
      id,
      errorMessage,
      required,
      disabled = false,
      min,
      max,
      step,
      dataTestId,
      inputMode,
    },
    ref,
  ) => {
    // Auto-set inputMode for number inputs if not explicitly provided
    const effectiveInputMode =
      inputMode ?? (type === "number" ? "decimal" : undefined);

    return (
      <InputWrapper className={className}>
        <StyledInput
          data-ls-disabled
          ref={ref}
          id={id}
          aria-label={placeholder}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onWheel={onWheel}
          hasError={errorMessage != null}
          required={required}
          disabled={disabled}
          data-testid={dataTestId ?? "input"}
          min={min}
          max={max}
          step={step}
          inputMode={effectiveInputMode}
        />
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </InputWrapper>
    );
  },
);

InputField.displayName = "InputField";
